<template>
  <div class="runner-input">
    <div class="input-container">
      <div class="header">
        <h1>Śledź Live</h1>
        <p class="event-name">{{ eventName }}</p>
      </div>
      
      <div class="input-section">
        <label for="runner-number">Wprowadź numer startowy:</label>
        <input 
          id="runner-number"
          v-model="runnerNumber" 
          type="number" 
          placeholder="255"
          class="runner-input-field"
          @keyup.enter="generateLink"
        >
        
        <button 
          @click="generateLink" 
          class="generate-btn"
          :disabled="!runnerNumber"
        >
          Wygeneruj link
        </button>
      </div>
      
      <div v-if="generatedUrl" class="link-section">
        <div class="generated-link">
          <label>Twój link śledzenia:</label>
          <div class="link-display">
            <input 
              ref="linkInput"
              :value="generatedUrl" 
              readonly 
              class="link-input"
            >
            <button @click="copyLink" class="copy-btn">
              {{ copied ? 'Skopiowano!' : 'Kopiuj' }}
            </button>
          </div>
        </div>
        
        <div class="share-section">
          <button @click="shareLink" class="share-btn primary">
            Udostępnij
          </button>
          <button @click="openTracking" class="track-btn">
            Otwórz śledzenie
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export default {
  name: 'RunnerInput',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const runnerNumber = ref('')
    const copied = ref(false)
    const linkInput = ref(null)
    
    const eventName = computed(() => route.params.eventName || 'Wydarzenie')
    
    const generatedUrl = computed(() => {
      if (!runnerNumber.value) return ''
      return `${window.location.origin}/${route.params.eventName}/${runnerNumber.value}`
    })
    
    const generateLink = () => {
      if (!runnerNumber.value) return
      // Link is generated automatically via computed property
    }
    
    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(generatedUrl.value)
        copied.value = true
        setTimeout(() => {
          copied.value = false
        }, 2000)
      } catch (err) {
        // Fallback for older browsers
        linkInput.value.select()
        document.execCommand('copy')
        copied.value = true
        setTimeout(() => {
          copied.value = false
        }, 2000)
      }
    }
    
    const shareLink = async () => {
      const shareData = {
        title: `Śledź Live - ${eventName.value}`,
        text: `Śledź mój postęp w wydarzeniu ${eventName.value} na żywo!`,
        url: generatedUrl.value
      }
      
      if (navigator.share) {
        try {
          await navigator.share(shareData)
        } catch (err) {
          // User cancelled sharing
        }
      } else {
        // Fallback - copy to clipboard
        copyLink()
      }
    }
    
    const openTracking = () => {
      router.push(`/${route.params.eventName}/${runnerNumber.value}`)
    }
    
    return {
      runnerNumber,
      copied,
      linkInput,
      eventName,
      generatedUrl,
      generateLink,
      copyLink,
      shareLink,
      openTracking
    }
  }
}
</script>

<style scoped>
.runner-input {
  min-height: 100vh;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: white;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.input-container {
  background: white;
  color: #2c3e50;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.header {
  text-align: center;
  margin-bottom: 32px;
}

.header h1 {
  margin: 0 0 8px 0;
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
}

.event-name {
  margin: 0;
  font-size: 1.2rem;
  color: #7f8c8d;
  font-weight: 500;
}

.input-section {
  margin-bottom: 32px;
}

.input-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.runner-input-field {
  width: 100%;
  padding: 16px;
  font-size: 1.2rem;
  border: 2px solid #bdc3c7;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.runner-input-field:focus {
  outline: none;
  border-color: #3498db;
}

.generate-btn {
  width: 100%;
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 600;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.generate-btn:hover:not(:disabled) {
  background: #2980b9;
}

.generate-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.link-section {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.generated-link {
  margin-bottom: 24px;
}

.generated-link label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.link-display {
  display: flex;
  gap: 8px;
}

.link-input {
  flex: 1;
  padding: 12px;
  font-size: 0.9rem;
  border: 2px solid #bdc3c7;
  border-radius: 8px;
  background: #f8f9fa;
}

.copy-btn {
  padding: 12px 16px;
  font-size: 0.9rem;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.copy-btn:hover {
  background: #229954;
}

.share-section {
  display: flex;
  gap: 12px;
}

.share-btn, .track-btn {
  flex: 1;
  padding: 16px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.share-btn.primary {
  background: #e74c3c;
  color: white;
}

.share-btn.primary:hover {
  background: #c0392b;
}

.track-btn {
  background: #f8f9fa;
  color: #2c3e50;
  border: 2px solid #bdc3c7;
}

.track-btn:hover {
  background: #ecf0f1;
  border-color: #95a5a6;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .runner-input {
    padding: 16px;
  }
  
  .input-container {
    padding: 24px;
  }
  
  .header h1 {
    font-size: 2rem;
  }
  
  .link-display {
    flex-direction: column;
  }
  
  .share-section {
    flex-direction: column;
  }
}
</style>